import { motion } from "framer-motion";
import { Gift } from "lucide-react";
import { cn } from "@/lib/utils";
import { MysteryBoxProps } from "./types";
import { getRarityGlow, formatRewardValue } from "./utils";

export function MysteryBox({
  id,
  state,
  isSelected,
  isCentered,
  reward,
  disabled,
  onSelect,
  ariaLabel,
}: MysteryBoxProps) {
  const isRevealed = state === "revealed";
  const isOpening = state === "opening";
  const showLidOpen = isOpening || isRevealed;
  const canInteract = !disabled && state === "closed";

  return (
    <motion.div
      layout
      layoutId={isCentered ? `box-${id}` : undefined}
      className={cn(
        "relative flex flex-col items-center",
        isCentered && "z-20 col-span-2 row-span-2 sm:col-span-3"
      )}
      animate={
        isCentered
          ? { scale: 1.15, y: 0 }
          : state === "closed" && !disabled
            ? { y: [0, -6, 0] }
            : { scale: 1, y: 0 }
      }
      transition={
        state === "closed" && !disabled
          ? { repeat: Infinity, duration: 2.5, ease: "easeInOut" }
          : { type: "spring", stiffness: 200, damping: 22 }
      }
    >
      <div
        className={cn(
          "absolute -bottom-2 h-3 w-3/4 rounded-full bg-black/40 blur-md",
          isCentered && "w-2/3"
        )}
      />

      <motion.button
        type="button"
        disabled={!canInteract}
        onClick={() => onSelect(id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (canInteract) onSelect(id);
          }
        }}
        aria-label={ariaLabel}
        aria-pressed={isSelected}
        aria-disabled={disabled}
        whileHover={canInteract ? { y: -8, scale: 1.05 } : undefined}
        whileTap={canInteract ? { scale: 0.98 } : undefined}
        animate={
          isSelected && !isOpening
            ? { scale: [1, 1.06, 1] }
            : isOpening
              ? { rotate: [0, -2, 2, -2, 2, 0] }
              : {}
        }
        transition={
          isSelected
            ? { repeat: Infinity, duration: 1.2 }
            : isOpening
              ? { duration: 0.6 }
              : {}
        }
        className={cn(
          "relative w-full max-w-[140px] focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0f1a] rounded-xl",
          disabled && !isSelected && "opacity-40 cursor-not-allowed",
          isSelected && "cursor-default"
        )}
      >
        {/* Glow burst when opening */}
        {(isOpening || isRevealed) && (
          <motion.div
            className="pointer-events-none absolute inset-0 -z-10 rounded-2xl bg-gradient-to-t from-amber-400/40 via-purple-500/30 to-blue-400/20 blur-xl"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{ opacity: 1, scale: 1.4 }}
            transition={{ duration: 0.8 }}
          />
        )}

        {/* Light rays */}
        {isRevealed && reward && (
          <>
            {Array.from({ length: 6 }).map((_, i) => (
              <motion.div
                key={i}
                className="pointer-events-none absolute left-1/2 top-1/2 h-24 w-1 origin-bottom bg-gradient-to-t from-amber-400/60 to-transparent"
                style={{ rotate: i * 60 }}
                initial={{ opacity: 0, scaleY: 0 }}
                animate={{ opacity: [0.3, 0.7, 0.3], scaleY: 1 }}
                transition={{ repeat: Infinity, duration: 2, delay: i * 0.1 }}
              />
            ))}
          </>
        )}

        <div
          className={cn(
            "relative overflow-hidden rounded-xl border-2 border-amber-500/50",
            "bg-gradient-to-b from-amber-900/80 via-amber-950 to-[#1a0f2e]",
            "shadow-[0_8px_32px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(251,191,36,0.3)]",
            "backdrop-blur-sm",
            isRevealed && reward && getRarityGlow(reward.rarity)
          )}
        >
          {/* Lid */}
          <motion.div
            className="absolute inset-x-0 top-0 z-10 h-[38%] origin-bottom rounded-t-xl border-b-2 border-amber-600/50 bg-gradient-to-b from-amber-600 to-amber-800"
            animate={{
              rotate: showLidOpen ? -55 : 0,
              y: showLidOpen ? -8 : 0,
            }}
            transition={{ type: "spring", stiffness: 120, damping: 14 }}
          >
            <div className="flex h-full items-center justify-center">
              <Gift className="h-6 w-6 text-amber-200/90" />
            </div>
          </motion.div>

          {/* Body */}
          <div className="flex min-h-[100px] flex-col items-center justify-center px-3 pb-4 pt-12 sm:min-h-[120px]">
            {isRevealed && reward ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.3, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ type: "spring", stiffness: 200, damping: 16 }}
                className="flex flex-col items-center text-center"
              >
                <span className="text-3xl sm:text-4xl">{reward.icon}</span>
                <p className="mt-1 text-xs font-bold text-amber-100 sm:text-sm">
                  {formatRewardValue(reward)}
                </p>
              </motion.div>
            ) : (
              <div className="flex flex-col items-center py-2">
                <span className="text-2xl opacity-80">📦</span>
                <span className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-amber-400/70">
                  Mystery
                </span>
              </div>
            )}
          </div>

          {/* Gold trim */}
          <div className="pointer-events-none absolute inset-x-2 bottom-2 h-0.5 rounded-full bg-amber-400/50" />
        </div>
      </motion.button>
    </motion.div>
  );
}
